import { useCallback, useEffect, useState } from "react";
import { Database, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { getWarehouseRebuild, startWarehouseRebuild, getWarehouseFreshness, getFeedWatch, syncWarehouseNow } from "@/lib/api";

// Banner post-deploy: warehouse storico incompleto -> CTA one-click per rigenerarlo.
// In un ambiente con dati completi (21/21 step) non renderizza nulla.
export const WarehouseBanner = () => {
  const [rb, setRb] = useState(null);
  const [fresh, setFresh] = useState(null);
  const [watch, setWatch] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(() => {
    getWarehouseRebuild().then(setRb).catch(() => {});
    getWarehouseFreshness().then(setFresh).catch(() => {});
    getFeedWatch().then(setWatch).catch(() => {});
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  const doSync = async () => {
    setSyncing(true);
    try {
      const r = await syncWarehouseNow(true);
      toast.success(r.synced?.length ? `Sincronizzate ${r.synced.length} serie` : "Nessuna serie da aggiornare");
      load();
    } catch { toast.error("Sincronizzazione fallita"); }
    setSyncing(false);
  };

  // FEED FERMO (rosso): oltre il limite per sorgente, non e' ritardo fisiologico.
  // E' il caso che ci e' costato un mese di barre .CT morte senza che nessuno lo sapesse.
  if (watch?.frozen > 0) {
    const bad = (watch.series || []).filter((s) => s.state === "fermo" || s.state === "assente")
      .sort((a, b) => (b.broker ? 1 : 0) - (a.broker ? 1 : 0));   // il feed del broker in cima
    return (
      <div data-testid="feed-banner-frozen"
        className="flex flex-wrap items-center gap-3 rounded-lg border border-red-500/50 bg-red-500/10 px-4 py-2.5 text-red-200">
        <Database className="h-5 w-5 shrink-0 text-red-300" />
        <p className="flex-1 text-sm leading-snug">
          <b className="font-semibold uppercase tracking-wide">
            {watch.brokerFrozen > 0 ? "Feed del broker fermo" : "Feed dati fermo"}
          </b>
          <span className="ml-2 opacity-90 font-mono text-xs">
            {watch.frozen} ferme{watch.late > 0 ? ` · ${watch.late} in ritardo (fisiologico)` : ""} ·{" "}
            {bad.slice(0, 3).map((s) => `${s.symbol} @ ${String(s.lastBar || "mai").slice(0, 10)}`
              + (s.sinceHours != null ? ` (${Math.round(s.sinceHours)}h)` : "")).join(" · ")}
            {watch.brokerFrozen > 0
              ? " — lo stadio dual-feed del Cancello e la sonda sul book stanno usando un feed vecchio"
              : " — la ricerca che legge il magazzino sta usando prezzi vecchi"}
          </span>
        </p>
        <Button size="sm" variant="outline" onClick={doSync} disabled={syncing}
          className="h-7 border-red-500/50 text-red-200 hover:bg-red-500/20"
          data-testid="feed-banner-sync-btn">
          {syncing ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <><RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Sincronizza</>}
        </Button>
      </div>
    );
  }

  // serie in RITARDO (ambra): oltre la tolleranza di sincronizzazione ma sotto il
  // limite di fermo — si ricompone da se', nessun Telegram
  if (fresh?.stale > 0) {
    const worst = (fresh.series || []).filter((s) => s.stale).slice(0, 3);
    return (
      <div data-testid="warehouse-banner-datastale"
        className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-2.5 text-amber-200">
        <Database className="h-5 w-5 shrink-0 text-amber-300" />
        <p className="flex-1 text-sm leading-snug">
          <b className="font-semibold uppercase tracking-wide">Magazzino in ritardo</b>
          <span className="ml-2 opacity-90 font-mono text-xs">
            {fresh.stale} serie oltre tolleranza ·{" "}
            {worst.map((s) => `${s.symbol} ${s.tf} @ ${String(s.lastBar || "mai").slice(0, 10)}`).join(" · ")}
            {" "}— nessuna e' ferma oltre il limite: il round-robin le riprende da solo
          </span>
        </p>
        <Button size="sm" variant="outline" onClick={doSync} disabled={syncing}
          className="h-7 border-amber-500/50 text-amber-200 hover:bg-amber-500/20"
          data-testid="warehouse-sync-btn">
          {syncing ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <><RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Sincronizza</>}
        </Button>
      </div>
    );
  }

  if (!rb || rb.completeSteps >= rb.totalSteps) return null;

  const rebuilding = rb.rebuild?.status === "running";
  const cur = (rb.plan || []).find((p) => p.id === rb.rebuild?.currentStep);
  const curPct = cur ? Math.min(100, Math.round((cur.count / cur.min) * 100)) : null;
  const hbAge = rb.rebuild?.hbTs ? Math.round(Date.now() / 1000 - rb.rebuild.hbTs) : null;
  const stale = rebuilding && hbAge !== null && hbAge > 480;

  const start = async () => {
    if (!window.confirm("Rigenerare lo storico dalle fonti esterne (Dukascopy, Binance, OKX, cTrader)? Gli step già completi vengono saltati; il totale può richiedere ore, prosegue da solo in background.")) return;
    try {
      const r = await startWarehouseRebuild();
      if (r.started) toast.success(`Rigenerazione avviata: ${r.steps} step`);
      else if (String(r.reason || "").includes("in corso")) toast.success("Sta già girando: battito rilevato, nessun riavvio necessario");
      else toast.info(r.reason || "già in corso");
      load();
    } catch { toast.error("Avvio rigenerazione fallito"); }
  };

  if (rebuilding && stale) {
    return (
      <div data-testid="warehouse-banner-stale"
        className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-2.5 text-amber-200">
        <Database className="h-5 w-5 shrink-0 text-amber-300" />
        <p className="flex-1 text-sm leading-snug">
          <b className="font-semibold uppercase tracking-wide">Rigenerazione senza battito</b>
          <span className="ml-2 opacity-90 font-mono text-xs">
            nessun battito da {Math.round(hbAge / 60)} min · step: {rb.rebuild.currentStep || "…"}
            {cur ? ` (${cur.count.toLocaleString("it-IT")}/${cur.min.toLocaleString("it-IT")} barre)` : ""} — dopo un riavvio riparte da sola entro ~2 min; se resta così, premi Riprendi.
          </span>
        </p>
        <Button size="sm" variant="outline" onClick={start}
          className="h-7 border-amber-500/50 text-amber-200 hover:bg-amber-500/20"
          data-testid="warehouse-banner-resume-btn">
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Riprendi
        </Button>
      </div>
    );
  }

  if (rebuilding) {
    return (
      <div data-testid="warehouse-banner-running"
        className="rounded-lg border border-cyan-500/40 bg-cyan-500/10 px-4 py-2.5 text-cyan-200">
        <div className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 shrink-0 animate-spin text-cyan-300" />
          <p className="flex-1 text-sm leading-snug">
            <b className="font-semibold uppercase tracking-wide">Rigenerazione warehouse in corso</b>
            <span className="ml-2 font-mono text-xs opacity-90">
              {rb.completeSteps}/{rb.totalSteps} fonti complete
              {hbAge !== null && <span data-testid="warehouse-banner-heartbeat"> · battito {hbAge}s fa</span>}
            </span>
          </p>
        </div>
        {cur && (
          <div className="mt-1.5 ml-8" data-testid="warehouse-banner-step-progress">
            <div className="flex items-center justify-between font-mono text-[11px] opacity-90">
              <span>step: {rb.rebuild.currentStep}</span>
              <span>{cur.count.toLocaleString("it-IT")}/{cur.min.toLocaleString("it-IT")} barre · {curPct}%</span>
            </div>
            <div className="mt-1 h-1 overflow-hidden rounded-full bg-cyan-950/60">
              <div className="h-full rounded-full bg-cyan-400 transition-[width] duration-700" style={{ width: `${curPct}%` }} />
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="warehouse-banner-incomplete"
      className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-2.5 text-amber-200">
      <Database className="h-5 w-5 shrink-0 text-amber-300" />
      <p className="flex-1 text-sm leading-snug">
        <b className="font-semibold uppercase tracking-wide">Warehouse storico incompleto</b>
        <span className="ml-2 opacity-90">
          {rb.completeSteps}/{rb.totalSteps} fonti dati presenti. Senza storico il Gate e i backtest
          non possono lavorare: rigeneralo dalle fonti originali.
        </span>
      </p>
      <Button size="sm" variant="outline" onClick={start}
        className="h-7 border-amber-500/50 text-amber-200 hover:bg-amber-500/20"
        data-testid="warehouse-banner-rebuild-btn">
        <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Rigenera ora
      </Button>
    </div>
  );
};

export default WarehouseBanner;
